/**
 * KONFIGURACJA EVENTÓW CZASOWYCH (sezonowych).
 *
 * Jeden wpis = jeden event. Event żyje tylko w oknie `startAt`–`endAt` przy
 * `enabled === true`. Cała logika "czy event trwa" przechodzi przez
 * `getActiveEvents()` / `isEventActive()` — JEDEN punkt prawdy. UI (kafel mapy,
 * sklep eventu, plakietka waluty, album) pyta tu, zamiast sprawdzać daty u siebie.
 *
 * Fundament wg `GAME_DESIGN.md` §5.8 + interfejs `EventDef` §7. Pierwsza
 * realizacja: "Wakacje Capibar" (`DESIGN_EVENT_CAPIBARA_SUMMER.md`).
 *
 * KOLEJNOŚĆ BUDOWY (roadmap z design doc):
 *   EV-0 (TERAZ) — ten plik + typ `EventDef` + helpery. Event WYŁĄCZONY.
 *   EV-1a — waluta `event_shells` (generyczny portfel `eventCurrencies` + HUD).
 *   EV-1b — biom "Plaża Capibar" (scena + destruktyble + kafel mapy).
 *   EV-2 — jajko `egg-event-capibara-summer` + 10 petów (defy w pets/eggs config).
 *   EV-3 — album + letni awatar.
 *   EV-5 — ustawić daty + `enabled: true` → event LIVE.
 */

import halloween from '../../server/api/src/game-config/halloween.config.json';
import type { ID } from '@/domain/types/common';
import type { EventDef } from '@/domain/types/event';

export const EVENTS: EventDef[] = [
  halloween.event,
  {
    id: 'event-capibara-summer',
    name: { pl: 'Wakacje Capibar', en: 'Capybara Summer' },

    // GO-LIVE: okno sezonu — event startuje 1 LIPCA 2026 i trwa do końca sierpnia.
    // Event powtarzalny co rok — przed kolejnym sezonem przesuń
    // rok. Po sezonie: enabled:false (content zostaje pod przyszły rok). MUSI być w syncu
    // z mirrorem serwera: server/api/src/game-config/events.ts.
    startAt: '2026-07-01T00:00:00Z',
    endAt: '2026-08-31T23:59:59Z',

    // ⭐ MASTER SWITCH — WŁĄCZONY (go-live). Event żyje TYLKO w oknie startAt–endAt
    // (zegar). Serwer ma mirror w game-config/events.ts — MUSZĄ być w syncu.
    enabled: true,

    // Content dodawany w kolejnych fazach. ID są tu zarezerwowane (referencja dla
    // albumu/filtra) — faktyczne defy petów/jajka pojawią się w EV-2.
    eggIds: ['egg-event-capibara-summer', 'egg-event-capibara-summer-gold'],
    // petIds = 10 capibar ALBUMU/PLAKATU (wszystkie HUGE-only od 2026-06-29 — „te 10
    // MUSZĄ być huge"). TYLKO te liczą się do kompletu/nagrody. Pełny roster eventu =
    // 62 (51 normalnych + 1 titanic `orange`) w pets.config.ts — NIE wchodzą do albumu.
    // Liczba MUSI zostać 10 (asercja albumPoster cols×rows == petIds.length, 5×2).
    petIds: [
      'pet-event-capibara-summer-soldier', // 1 common
      'pet-event-capibara-summer-pirate', // 2 common
      'pet-event-capibara-summer-ninja', // 3 uncommon
      'pet-event-capibara-summer-astronaut', // 4 uncommon
      'pet-event-capibara-summer-knight', // 5 rare
      'pet-event-capibara-summer-dragon', // 6 rare
      'pet-event-capibara-summer-cyborg', // 7 epic
      'pet-event-capibara-summer-superhero', // 8 legendary
      'pet-event-capibara-summer-king', // 9 legendary HUGE (headline)
      'pet-event-capibara-summer-sun', // 10 mythic (HUGE)
    ],

    // Waluta muszelek (EV-1a). Saldo w generycznym `save.eventCurrencies`
    // pod kluczem `id` — zero kolumn DB per event. Ikona PNG dochodzi w EV-1b
    // (na razie `<Icon>` użyje fallbacku). Wzorzec pod przyszłe eventy:
    // Halloween → { id: 'event_skulls', ... }, Zima → { id: 'event_flakes', ... }.
    currency: {
      id: 'event_shells',
      name: { pl: 'Muszelki', en: 'Seashells' },
      iconKey: '/ui/ui_shell.webp',
      fallbackEmoji: '🐚',
    },

    // Czasowy biom plaży dodawany w EV-1b:
    biomeId: 'biome-event-capibara-beach',

    // Album Wakacyjny (EV-3): 10 slotów = petIds powyżej. Komplet → 1000 gemów +
    // letni awatar (para chłopiec/dziewczynka). Liczba gemów strojona w configu.
    album: {
      rewardGems: 1000,
      rewardAvatarSkinIds: [
        'avatar_event_capibara_summer_boy',
        'avatar_event_capibara_summer_girl',
      ],
    },

    // Graficzny „Plakat Wakacyjny" (5×2, odsłaniany jak puzzle). Grafika:
    // assets/events/capibara_summer/album/album_poster_capibara_summer.webp.
    // Komórki w kolejności petIds (żołnierz→…→sun) — patrz album_poster_prompt.md.
    albumPoster: {
      textureKey: 'album_poster_capibara_summer',
      cols: 5,
      rows: 2,
    },

    // Dzienna paczka od Capibary Handlarki w biomie eventowym. Kwoty odpowiadają
    // 3-9 zwykłym jajkom eventowym (25 muszelek) i łagodzą grind bez rozdawania
    // Huge/Titanic. Serwer ma mirror w `server/api/src/game-config/events.ts`.
    dailyReward: {
      currencyId: 'event_shells',
      amountsByStreak: [75, 90, 110, 130, 150, 175, 225],
    },

    // Połów z mola — mały bonus bez modala, pokazany w Phaserze jako animacja
    // muszelek nad wodą. Handlarka zostaje główną dużą paczką dzienną.
    pierFishing: {
      currencyId: 'event_shells',
      minAmount: 15,
      maxAmount: 35,
      cooldownMs: 4 * 60 * 60 * 1000,
    },

    // Brak modyfikatorów (decyzja E4 — bez x2 monet). Łatwo dodać później:
    // modifiers: { globalCoinMult: 2 },
  },
];

/**
 * Zwraca eventy aktywne w danym momencie: `enabled === true` ORAZ
 * `startAt <= now <= endAt`. To JEDYNE źródło prawdy "czy event trwa".
 *
 * @param now czas odniesienia (domyślnie teraz). Wstrzykiwalny dla testów /
 *            przyszłego serwerowego `/time`.
 */
export function getActiveEvents(now: Date = new Date()): EventDef[] {
  const t = now.getTime();
  return EVENTS.filter((e) => {
    if (!e.enabled) return false;
    const start = Date.parse(e.startAt);
    const end = Date.parse(e.endAt);
    // Defensywnie: zła data w configu = event nieaktywny (zamiast crasha).
    if (Number.isNaN(start) || Number.isNaN(end)) return false;
    return t >= start && t <= end;
  });
}

/**
 * Lead ZAPOWIEDZI: ile przed `startAt` event pokazuje się w menu jako „wkrótce" —
 * kafel widoczny, ale NIE-otwieralny, z licznikiem do startu (buduje wyczekiwanie).
 * 7 dni: seasonalny event widać tydzień wcześniej. Server gate (`isEventActive`) dalej
 * blokuje wejście/muszelki/jajka do `startAt` — teaser jest czysto wizualny (klient).
 */
export const EVENT_TEASER_LEAD_MS = 7 * 24 * 60 * 60 * 1000;

const EVENTS_BY_ID = new Map<ID, EventDef>(EVENTS.map((event) => [event.id, event]));
const EVENTS_BY_BIOME_ID = new Map<ID, EventDef>(
  EVENTS.flatMap((event) => (event.biomeId ? [[event.biomeId, event] as const] : [])),
);

/**
 * Eventy w oknie ZAPOWIEDZI: `enabled`, jeszcze przed `startAt`, ale w zasięgu leadu
 * (`startAt − EVENT_TEASER_LEAD_MS ≤ now < startAt`). Używane przez `BiomeMapModal` do
 * kafla „wkrótce". NIE przez preload/unlock — biom ładuje się i wchodzi dopiero gdy
 * event AKTYWNY (`getActiveEventBiomeIds`).
 */
export function getUpcomingEvents(now: Date = new Date()): EventDef[] {
  const t = now.getTime();
  return EVENTS.filter((e) => {
    if (!e.enabled) return false;
    const start = Date.parse(e.startAt);
    if (Number.isNaN(start)) return false;
    return t < start && t >= start - EVENT_TEASER_LEAD_MS;
  });
}

/**
 * Biomy eventów AKTYWNYCH teraz (po `biomeId`). Używane przez kafel mapy
 * (`BiomeMapModal`) i preload (`PreloadScene`) — biom eventowy pojawia się
 * i ładuje TYLKO w oknie eventu. Poza oknem: `[]` → brak kafla, brak preloadu.
 */
export function getActiveEventBiomeIds(now: Date = new Date()): ID[] {
  return getActiveEvents(now)
    .map((e) => e.biomeId)
    .filter((id): id is ID => id != null);
}

/**
 * Biomy WSZYSTKICH eventów w configu — bez patrzenia na zegar i bez patrzenia na
 * `enabled`. To NIE jest „co jest otwarte", tylko „czyje pety mogą leżeć na koncie
 * gracza".
 *
 * Pet zdobyty w evencie zostaje w kolekcji NA ZAWSZE (design doc: „po evencie nic
 * nie kasujemy"), więc jego grafika musi być ładowana na zawsze — inaczej po
 * `endAt` wyposażona capibara zamienia się w różowy placeholder, a reveal
 * wyklucia w ogóle pomija sprite'a. Patrz `PreloadScene`.
 *
 * DLACZEGO bez zegara i bez `enabled`: oba te sygnały PRZESTAJĄ być prawdziwe dla
 * starych petów. Config eventu powtarzalnego jest co roku przestawiany na nowy
 * sezon (`startAt` w przyszłość) i dostaje `enabled:false` po sezonie — a pety
 * z zeszłego roku dalej wiszą w plecakach. Gdyby ładowanie grafik zależało od
 * któregokolwiek z tych pól, znikałyby przy każdym przestawieniu dat.
 *
 * KOSZT: grafiki petów eventu to ~0,8 MB (62 capibary) wobec 23 MB zwykłych
 * biomów. Rośnie o tyle z każdym kolejnym eventem — świadoma cena za to, że
 * trofea dziecka nie znikają.
 *
 * UWAGA przy stagingu przyszłego eventu: defy petów wchodzą do `pets.config.ts`
 * dopiero razem z grafikami. Wcześniej preload poleci po pliki, których nie ma
 * (fail-soft — `loaderror` → placeholder — ale śmieci w konsoli). Tak samo
 * robiliśmy z Atlantydą: staging trzymany poza `src/` do go-live.
 */
export function getEventBiomeIds(): ID[] {
  return [...EVENTS_BY_BIOME_ID.keys()];
}

/**
 * Strażnik formatu dat (Codex EV-0 LOW): `startAt`/`endAt` MUSZĄ być w UTC z
 * sufiksem `Z` (`YYYY-MM-DDTHH:mm:ssZ`). Data bez strefy → `Date.parse` traktuje
 * ją jako czas LOKALNY środowiska, co przesuwałoby start/koniec eventu zależnie
 * od klienta. Sprawdzamy RAZ przy ładowaniu modułu (dev warning) — tania asekuracja
 * zanim event pójdzie LIVE (EV-5). Nie rzuca: nie wywraca gry przez literówkę w configu.
 */
const UTC_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
for (const e of EVENTS) {
  for (const [field, value] of [['startAt', e.startAt], ['endAt', e.endAt]] as const) {
    if (!UTC_ISO_RE.test(value)) {
      console.warn(
        `[events.config] Event '${e.id}': ${field}='${value}' nie jest w UTC ` +
        `(oczekiwany format 'YYYY-MM-DDTHH:mm:ssZ'). Bez 'Z' okno eventu przesunie ` +
        `się zależnie od strefy klienta. Popraw przed włączeniem eventu (EV-5).`,
      );
    }
  }
  // Plakat-puzzle: liczba kawałków (cols×rows) MUSI równać się liczbie petIds,
  // inaczej nakładka reveal rozjedzie się z grafiką (kawałek i ↔ petIds[i]).
  if (e.albumPoster) {
    const cells = e.albumPoster.cols * e.albumPoster.rows;
    if (cells !== e.petIds.length) {
      console.warn(
        `[events.config] Event '${e.id}': albumPoster ${e.albumPoster.cols}×${e.albumPoster.rows}=${cells} ` +
        `≠ petIds.length=${e.petIds.length}. Nakładka plakatu rozjedzie się z grafiką — dopasuj siatkę do liczby petów.`,
      );
    }
  }
}

/** Czy konkretny event jest teraz aktywny (wygodny skrót do `getActiveEvents`). */
export function isEventActive(eventId: ID, now: Date = new Date()): boolean {
  return getActiveEvents(now).some((e) => e.id === eventId);
}

/** Definicja eventu po ID (niezależnie od tego, czy aktywny). */
export function getEventById(eventId: ID): EventDef | undefined {
  return EVENTS_BY_ID.get(eventId);
}

/** Definicja eventu po biomie eventowym (np. klik NPC na plaży → event summer). */
export function getEventByBiomeId(biomeId: ID): EventDef | undefined {
  return EVENTS_BY_BIOME_ID.get(biomeId);
}

/**
 * Event, którego Album ma być pokazany w UI (menu „Album" + modal).
 *
 * Album jest STAŁY (kolekcja dostępna też poza oknem eventu). Priorytet wyboru:
 *   1. aktywny event z albumem (gdy event trwa),
 *   2. poza oknem — event z albumem o NAJWIĘKSZYM postępie gracza (jego kolekcja;
 *      tu też trafia komplet 10/10 do odebrania po evencie — serwer na to pozwala),
 *   3. fallback — pierwszy event z albumem w configu (pokazuje kolekcję 0/X jako
 *      zapowiedź, zanim event ruszy).
 * `undefined` tylko gdy w configu NIE ma żadnego eventu z albumem.
 *
 * @param playerEvents `PlayerSave.events` (stan albumu gracza per eventId)
 */
export function resolveAlbumEvent(
  playerEvents: Record<string, { album?: { discovered: ID[]; claimed: boolean } }>,
  now: Date = new Date(),
): EventDef | undefined {
  const albumEvents = EVENTS.filter((e) => e.album);
  if (albumEvents.length === 0) return undefined;
  const active = getActiveEvents(now).find((e) => e.album);
  if (active) return active;
  // Poza oknem: event z największym postępem; przy remisie (np. 0/X) pierwszy z configu.
  let best = albumEvents[0];
  let bestN = playerEvents[best!.id]?.album?.discovered.length ?? 0;
  for (const e of albumEvents) {
    const n = playerEvents[e.id]?.album?.discovered.length ?? 0;
    if (n > bestN) {
      best = e;
      bestN = n;
    }
  }
  return best;
}
